import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'
import { uppercaseFirst } from '@/lib/helpers/helpers'
import ratioAccommodationExtensionMapItemObject from '@/entities/ratioAccommodationExtensionMapItem/interface'

import {
  ALL_EXTENSIONS_QUERY,
  SINGLE_EXTENSION_QUERY,
  EXTENSIONS_DEPENDENTS_QUERY,
  CREATE_EXTENSION_MUTATION,
  UPDATE_EXTENSION_MUTATION,
  DELETE_EXTENSION_MUTATION,
  FORCE_DELETE_EXTENSION_MUTATION,
  ADD_EXTENSION_ACCOMMODATION_MAP_ITEMS_MUTATION,
  REMOVE_EXTENSION_ACCOMMODATION_MAP_ITEMS_MUTATION,
  CREATE_EXTENSION_ACCOMMODATION_MAP_ITEMS_MUTATION,
  DELETE_EXTENSION_ACCOMMODATION_MAP_ITEMS_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const ratioTravelExtensionRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_EXTENSION_QUERY, variables).then((data: any) => {
      return hydrateObject(data.extension)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_EXTENSIONS_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.extensions)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_EXTENSION_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.extension),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_EXTENSIONS_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.extensions),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  getDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    const variables = { id: id, ...includeVars }
    return graphQLClient.request(EXTENSIONS_DEPENDENTS_QUERY, variables).then((data: any) => {
      return data.groupedDependents
    })
  },

  useDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includeVars }), [id])
    const { data, error, mutate } = useSWR([EXTENSIONS_DEPENDENTS_QUERY, variables], {
      revalidateOnFocus: false,
    })
    return {
      data: !data ? data : data.groupedDependents,
      isLoading: !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_EXTENSION_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.extension)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_EXTENSION_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.extension)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_EXTENSION_MUTATION, variables).then((data: any) => {
      return data.deleteRatioTravelExtension
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_EXTENSION_MUTATION, variables).then((data: any) => {
      return data.forceDeleteRatioTravelExtension
    })
  },

  assignAccommodationMapItems: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        accommodationMapItemIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_EXTENSION_ACCOMMODATION_MAP_ITEMS_MUTATION, variables)
  },

  unassignAccommodationMapItems: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        accommodationMapItemIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_EXTENSION_ACCOMMODATION_MAP_ITEMS_MUTATION, variables)
  },

  createAccommodationMapItems: async (id: string, items: Array<ratioAccommodationExtensionMapItemObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        accommodationMapItems: items,
      },
    }
    return graphQLClient.request(CREATE_EXTENSION_ACCOMMODATION_MAP_ITEMS_MUTATION, variables)
  },

  deleteAccommodationMapItems: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        accommodationMapItemIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_EXTENSION_ACCOMMODATION_MAP_ITEMS_MUTATION, variables)
  },
}

export default ratioTravelExtensionRepository
